import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Badge,
  Card,
  Heading,
  SimpleGrid,
  Input,
  Spinner,
  Alert,
} from '@chakra-ui/react';
import { toaster } from '../../components/ui/toaster';
import { FiSearch, FiShield, FiCheck, FiInfo } from 'react-icons/fi';
import { SystemStandard } from '../../types';
import { useTranslation } from 'react-i18next';

const { Body: CardBody, Header: CardHeader } = Card;

interface StandardSelectionStepProps {
  selectedStandard: SystemStandard | null;
  onStandardSelect: (standard: SystemStandard) => void;
}

// Mock standards data - replace with API call
const MOCK_STANDARDS: SystemStandard[] = [
  {
    id: '1',
    name: 'ISO 27001:2022 Information Security Management',
    description: 'International standard for information security management systems (ISMS)',
    version: '2022',
    standard_type: 'ISO27001_2022',
    sections_data: {},
    is_active: true,
    created_by: 1,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    review_prompt_template: 'Comprehensive security management review',
  },
  {
    id: '2',
    name: 'SOC 2 Type II Trust Services Criteria',
    description: 'Service Organization Control 2 for security, availability, processing integrity, confidentiality, and privacy',
    version: '2017',
    standard_type: 'SOC2_TYPEII',
    sections_data: {},
    is_active: true,
    created_by: 1,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    review_prompt_template: 'Trust services criteria assessment',
  },
  {
    id: '3',
    name: 'NIST Cybersecurity Framework',
    description: 'Framework for improving critical infrastructure cybersecurity',
    version: '1.1',
    standard_type: 'NIST_CSF',
    sections_data: {},
    is_active: true,
    created_by: 1,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    review_prompt_template: 'Cybersecurity framework assessment',
  },
  {
    id: '4',
    name: 'General Data Protection Regulation (GDPR)',
    description: 'European Union regulation on data protection and privacy',
    version: '2018',
    standard_type: 'GDPR',
    sections_data: {},
    is_active: true,
    created_by: 1,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    review_prompt_template: 'Data protection compliance review',
  },
];

const StandardSelectionStep: React.FC<StandardSelectionStepProps> = ({
  selectedStandard,
  onStandardSelect,
}) => {
  const [standards, setStandards] = useState<SystemStandard[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<SystemStandard[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    loadStandards();
  }, []);

  useEffect(() => {
    filterStandards();
  }, [standards, searchTerm]);

  const loadStandards = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      console.log('Fetching standards from /api/system-standards...');
      const response = await fetch('/api/system-standards', {
        credentials: 'include',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(t('createReview.steps.failedToLoadStandardsStatus', { status: response.status, error: errorText }));
      }

      const data = await response.json();
      console.log('Standards data received:', data);
      setStandards(data || []); // The API returns the array directly, not wrapped in a 'standards' property
    } catch (error) {
      console.error('Error loading standards:', error);
      toaster.create({
        title: t('createReview.steps.errorLoadingStandards'),
        description: error instanceof Error ? error.message : t('createReview.steps.failedToLoadAvailableStandardsTryAgain'),
        type: 'error',
        duration: 5000,
      });
      // Fallback to mock data if API fails
      setStandards(MOCK_STANDARDS);
    } finally {
      setIsLoading(false);
    }
  };

  const filterStandards = () => {
    if (!searchTerm.trim()) {
      setFilteredStandards(standards);
      return;
    }

    const filtered = standards.filter(standard =>
      standard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standard.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      standard.standard_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStandards(filtered);
  };

  const handleStandardSelect = (standard: SystemStandard) => {
    onStandardSelect(standard);
    toaster.create({
      title: t('createReview.steps.standardSelected'),
      description: t('createReview.steps.standardSelectedForReview', { name: standard.name }),
      type: 'success',
      duration: 3000,
    });
  };

  const getStandardDescription = (standard: SystemStandard) => {
    switch (standard.standard_type) {
      case 'ISO27001_2022':
        return t('createReview.steps.iso27001Description');
      case 'SOC2_TYPEII':
        return t('createReview.steps.soc2Description');
      case 'NIST_CSF':
        return t('createReview.steps.nistCsfDescription');
      case 'GDPR':
        return t('createReview.steps.gdprDescription');
      default:
        return standard.description;
    }
  };

  const getStandardIcon = (standardType: string) => {
    switch (standardType) {
      case 'ISO27001_2022':
        return '🔒';
      case 'SOC2_TYPEII':
        return '🏢';
      case 'NIST_CSF':
        return '🛡️';
      case 'GDPR':
        return '📋';
      default:
        return '📄';
    }
  };

  const getStandardColor = (standardType: string) => {
    switch (standardType) {
      case 'ISO27001_2022':
        return 'blue';
      case 'SOC2_TYPEII':
        return 'green';
      case 'NIST_CSF':
        return 'purple';
      case 'GDPR':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="md" mb={2}>
            {t('createReview.steps.selectComplianceStandardTitle')}
          </Heading>
          <Text color="gray.600">
            {t('createReview.steps.selectComplianceStandardLoadingSubtitle')}
          </Text>
        </Box>
        
        <VStack gap={4} py={12}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">{t('createReview.steps.loadingAvailableStandards')}</Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          {t('createReview.steps.selectComplianceStandardTitle')}
        </Heading>
        <Text color="gray.600">
          {t('createReview.steps.selectComplianceStandardSubtitle')}
        </Text>
      </Box>

      {/* Search */}
      <Box position="relative">
        <Icon
          as={FiSearch}
          position="absolute"
          left="12px"
          top="50%"
          transform="translateY(-50%)"
          color="gray.400"
          zIndex={1}
          pointerEvents="none"
        />
        <Input
          placeholder={t('createReview.steps.searchStandardsPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          bg="white"
          pl="40px"
        />
      </Box>

      {/* Selected Standard Display */}
      {selectedStandard && (
        <Card.Root variant="elevated" borderColor="brand.200">
          <CardHeader>
            <HStack justify="space-between">
              <HStack gap={3}>
                <Text fontSize="2xl">{getStandardIcon(selectedStandard.standard_type)}</Text>
                <VStack align="start" gap={1}>
                  <Text fontWeight="semibold">{selectedStandard.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {t('createReview.steps.versionLabel', { version: selectedStandard.version })}
                  </Text>
                </VStack>
              </HStack>
              <Badge colorPalette="green" variant="subtle">
                <HStack gap={1}>
                  <Icon as={FiCheck} w={3} h={3} />
                  <Text>{t('createReview.steps.selected')}</Text>
                </HStack>
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <Text color="gray.600" mb={3}>
              {getStandardDescription(selectedStandard)}
            </Text>
            <Button
              variant="outline"
              colorPalette="red"
              size="sm"
              onClick={() => onStandardSelect(null as any)}
            >
              {t('createReview.steps.changeSelection')}
            </Button>
          </CardBody>
        </Card.Root>
      )}

      {/* Standards Grid */}
      {!selectedStandard && (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {filteredStandards.map((standard) => (
            <Card.Root key={standard.id}
              variant="outline"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: 'brand.300',
                boxShadow: 'md',
                transform: 'translateY(-2px)',
              }}
              onClick={() => handleStandardSelect(standard)}
            >
              <CardBody>
                <VStack gap={3} align="stretch">
                  <HStack gap={3}>
                    <Text fontSize="2xl">{getStandardIcon(standard.standard_type)}</Text>
                    <VStack align="start" gap={1} flex={1}>
                      <Text fontWeight="semibold" fontSize="sm">
                        {standard.name}
                      </Text>
                      <Badge
                        colorPalette={getStandardColor(standard.standard_type)}
                        variant="subtle"
                        size="sm"
                      >
                        {standard.standard_type}
                      </Badge>
                    </VStack>
                  </HStack>
                  
                  <Text fontSize="sm" color="gray.600" lineClamp={2}>
                    {getStandardDescription(standard)}
                  </Text>
                  
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      {t('createReview.steps.versionLabel', { version: standard.version })}
                    </Text>
                    <Icon as={FiShield} w={4} h={4} color="gray.400" />
                  </HStack>
                </VStack>
              </CardBody>
            </Card.Root>
          ))}
        </SimpleGrid>
      )}

      {/* No Results */}
      {!selectedStandard && filteredStandards.length === 0 && searchTerm && (
        <Alert.Root status="info" borderRadius="md">
          <Alert.Indicator>
            <Icon as={FiInfo} />
          </Alert.Indicator>
          <Box>
            <Alert.Title>{t('createReview.steps.noStandardsFound')}</Alert.Title>
            <Alert.Description>
              {t('createReview.steps.noStandardsFoundDescription')}
            </Alert.Description>
          </Box>
        </Alert.Root>
      )}

      {/* Standards Info */}
      <Alert.Root status="info" borderRadius="md">
        <Alert.Indicator>
          <Icon as={FiInfo} />
        </Alert.Indicator>
        <Box>
          <Alert.Title>{t('createReview.steps.aboutComplianceStandards')}</Alert.Title>
          <Alert.Description>
            {t('createReview.steps.aboutComplianceStandardsDescription')}
          </Alert.Description>
        </Box>
      </Alert.Root>
    </VStack>
  );
};

export default StandardSelectionStep; 
